import { useRef, useState } from 'react'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { AtNavbarLinkProps } from '../../atoms'
import { textColorTransition } from '../../atoms/at-tabs/shared/classes'
import { Highlight } from '../../atoms/at-tabs/shared/highlight'
import { AnchorProps } from '@headlessui/react/dist/internal/floating'

export type openDirectionType = 'left' | 'right'

export interface MlNavbarDropdownProps {
  title: string
  dropdownLinks: AtNavbarLinkProps[]
  openDirection?: openDirectionType
}

export const MlNavbarDropdown = ({ title, dropdownLinks, openDirection = 'right' }: MlNavbarDropdownProps) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const activeElementRef = useRef<HTMLElement | Element | null>(null)

  const anchorDirection: AnchorProps =
    openDirection === 'right' ? { to: 'bottom start', offset: -82 } : { to: 'bottom end', offset: 82 }

  const handleEnter = (isOpen: boolean) => {
    if (!isOpen) {
      triggerRef.current?.click()
      activeElementRef.current = document.activeElement
    }
  }

  const handleLeave = (isOpen: boolean) => {
    isOpen && activeElementRef.current === triggerRef.current && triggerRef.current?.click()
  }

  //the library headlessui has a bug that make the Popover enabling :focus-visible state on mouse interaction
  const focusFix = () => {
    activeElementRef.current = document.activeElement
    requestAnimationFrame(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    })
  }

  return (
    <Popover
      className={`relative group relative h-full leading-1.5 inline-flex 
        justify-center items-center gap-2 cursor-pointer min-w-17 group 
        text-neutrals-800 ${textColorTransition}`}
      role="navigation"
    >
      {({ open }) => (
        <>
          <div
            className="w-full h-full px-16"
            onMouseEnter={() => handleEnter(open)}
            onMouseLeave={() => handleLeave(open)}
          >
            <PopoverButton
              className="w-full h-full flex flex-grow items-center justify-center"
              ref={triggerRef}
              onClick={focusFix}
              aria-haspopup="true"
            >
              <div>
                <Highlight className="absolute -bottom-[1px] group-data-[open]:!w-[calc(100%-4rem)] group-data-[open]:bg-current" />
                <p
                  className={`text-start font-regular ${
                    open && 'text-neutrals-700'
                  } group-hover:font-bold group-hover:text-primary-500`}
                  aria-label={title}
                >
                  {title}
                </p>
              </div>
            </PopoverButton>
            <PopoverPanel
              anchor={anchorDirection}
              className={`grid grid-flow-col gap-x-[3.875rem] justify-between py-6 px-[5.125rem] z-20 
                shadow-level-4 bg-neutrals-white grid-rows-${Math.min(4, dropdownLinks.length)}`}
              role="menu"
            >
              {dropdownLinks.map((link, index) => (
                <DropdownLink key={index} {...link} />
              ))}
            </PopoverPanel>
          </div>
        </>
      )}
    </Popover>
  )
}

const DropdownLink = ({ label, href, onTrackClick, disabled, target }: AtNavbarLinkProps) => {
  const [isHovered, setIsHovered] = useState(false)
  return (
    <div className="h-[3.375rem] pt-1.5 pb-1 w-full">
      <a
        className="font-regular text-14 leading-[1.3rem] text-neutrals-800 hover:text-neutrals-900 block w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(event) => {
          if (onTrackClick) onTrackClick(event)
        }}
        href={!disabled ? href : undefined}
        target={target}
        aria-disabled={disabled}
        role="menuitem"
        aria-label={label}
      >
        <span className="min-w-30">{label}</span>
      </a>
      {!disabled && <Highlight selected={isHovered} className="relative -bottom-[10px]" />}
    </div>
  )
}
